// BUSwitcher — top-bar dropdown to pin the current session to a Business Unit.
// Hidden when the user has < 2 accessible BUs (no meaningful choice).
import { useBU } from '@/contexts/BUContext';
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent,
  DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import { Button } from '@/components/ui/button';
import { Network, Check, Globe } from 'lucide-react';

const LEVEL_LABEL = {
  company: 'Company',
  business_unit: 'BU',
  department: 'Dept',
};

export default function BUSwitcher() {
  const { units, selectedId, setBU } = useBU();

  if (!units || units.length < 2) return null;

  const selected = units.find((u) => u.id === selectedId);
  const label = selected ? selected.name : 'All Business Units';

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className="border-slate-700 text-slate-200 hover:bg-slate-800 max-w-[180px]"
          data-testid="bu-switcher-trigger"
          title="Filter every list, chart and KPI by business unit"
        >
          <Network className="w-3.5 h-3.5 mr-1.5 text-cyan-400" />
          <span className="truncate text-xs">{label}</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        className="bg-slate-900 border-slate-700 min-w-[220px]"
        data-testid="bu-switcher-menu"
      >
        <DropdownMenuLabel className="text-[10px] uppercase tracking-wide text-slate-500">
          Business unit context
        </DropdownMenuLabel>
        <DropdownMenuItem
          onClick={() => setBU(null)}
          className={selectedId ? '' : 'bg-slate-800'}
          data-testid="bu-switcher-item-all"
        >
          <Globe className="w-3.5 h-3.5 mr-2 text-slate-400" />
          <span className="flex-1">All Business Units</span>
          {!selectedId && <Check className="w-3.5 h-3.5 text-cyan-400" />}
        </DropdownMenuItem>
        <DropdownMenuSeparator className="bg-slate-800" />
        {units.map((u) => (
          <DropdownMenuItem
            key={u.id}
            onClick={() => setBU(u.id)}
            className={selectedId === u.id ? 'bg-slate-800' : ''}
            data-testid={'bu-switcher-item-' + u.id}
          >
            <Network className="w-3.5 h-3.5 mr-2 text-slate-400" />
            <div className="flex-1 min-w-0">
              <div className="truncate text-sm text-slate-100">{u.name}</div>
              <div className="text-[10px] text-slate-500 uppercase">
                {LEVEL_LABEL[u.level] || u.level}
                {u.code ? ' · ' + u.code : ''}
              </div>
            </div>
            {selectedId === u.id && <Check className="w-3.5 h-3.5 text-cyan-400 ml-2" />}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
